import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { advertisementService } from '../../services/advertisementService';

const StatCard = ({ title, value, icon, color = 'blue' }) => (
  <div className="bg-white rounded-lg shadow-md p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
        <p className="text-3xl font-bold text-gray-900">{value}</p>
      </div>
      <div className={`text-${color}-600`}>
        {icon}
      </div>
    </div>
  </div>
);

const AdvertisementStatistics = () => {
  const { t } = useTranslation();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const response = await advertisementService.getStatistics();
      
      if (response.success) {
        setStatistics(response.data);
      } else {
        setError(response.message || t('FailedToLoadAdvertisements'));
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      setError(t('FailedToLoadAdvertisements'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3">{t('Loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  const stats = statistics || {
    total_advertisements: 0,
    pending_advertisements: 0,
    approved_advertisements: 0,
    active_advertisements: 0,
    rejected_advertisements: 0,
    total_views: 0,
    total_clicks: 0,
    total_revenue: 0,
    average_views_per_ad: 0,
    click_through_rate: 0,
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        {t('AdvertisementStatistics')}
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Advertisements */}
        <StatCard
          title={t('AllAdvertisements')}
          value={stats.total_advertisements}
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          }
          color="blue"
        />

        {/* Pending Advertisements */}
        <StatCard
          title={t('PendingAdvertisements')}
          value={stats.pending_advertisements}
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          color="yellow"
        />

        {/* Active Advertisements */}
        <StatCard
          title={t('ActiveAdvertisements')}
          value={stats.active_advertisements}
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          color="green"
        />

        {/* Rejected Advertisements */}
        <StatCard
          title={t('RejectedAdvertisements')}
          value={stats.rejected_advertisements}
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          }
          color="red"
        />

        {/* Total Views */}
        <StatCard
          title={t('TotalViews')}
          value={stats.total_views?.toLocaleString() || 0}
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          }
          color="indigo"
        />

        {/* Total Clicks */}
        <StatCard
          title={t('TotalClicks')}
          value={stats.total_clicks?.toLocaleString() || 0}
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
          }
          color="purple"
        />

        {/* Total Revenue */}
        <StatCard
          title={t('TotalRevenue')}
          value={`$${stats.total_revenue?.toLocaleString() || 0}`}
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          }
          color="green"
        />

        {/* Click Through Rate */}
        <StatCard
          title={t('ClickThroughRate')}
          value={`${stats.click_through_rate?.toFixed(2) || 0}%`}
          icon={
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
          color="blue"
        />
      </div>

      {/* Additional Statistics */}
      <div className="mt-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          إحصائيات إضافية
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('AverageViewsPerAd')}
            </h3>
            <p className="text-4xl font-bold text-blue-600">
              {stats.average_views_per_ad?.toFixed(1) || 0}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('ApprovedAdvertisements')}
            </h3>
            <p className="text-4xl font-bold text-green-600">
              {stats.approved_advertisements || 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvertisementStatistics;